import React, { ReactNode } from "react";

// eslint-disable-next-line @typescript-eslint/naming-convention
export type MODAL_VIEWS =
  | "AGENDA_EVENT_ITEM"
  | "AGENDA_EVENT_FEEDBACK"
  | "CONFIRM_ONE_TO_ONE_MODAL"
  | "CONFIRM_ONE_TO_ONE_FIRST_SESSION_MODAL"
  | "CONFIRM_ONE_TO_ONE_SECOND_SESSION_MODAL"
  | "CONFIRM_DIPLOMA_REQUEST_MODAL"
  | "CONFIRM_AVAILABILITY_SLOTS_MODAL"
  | "DO_EXERCISE_MODAL"
  | "DO_EXERCISE_ACTION_MODAL"
  | "CONFIRM_MOCK_TEST_MODAL"
  | "CONFIRM_LIVE_CORRECTION_BOOKING_MODAL"
  | "LIVE_CORRECTION_BOOKING_ERROR_MODAL"
  | "REQUEST_DIPLOMA_NOTIFICATION_MODAL"
  | "CHANGE_EVENT_DESKTOP_MODAL"
  | "FEEDBACK_RATING_MODAL"
  | "EXERCISE_V2_MODAL"
  | "UPSELLING_MODAL"
  | "MATERIALS_FILTERS_MODAL";

type Props = {
  headerCss?: never;
  renderHeader?: React.ReactNode;
  heading?: string;
  subheading?: string;
  footer?: ReactNode;
  trigger?: ReactNode;
  triggerAsChild?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (value: boolean) => void;
  modal?: boolean;
  contentClass?: string;
};
interface State {
  view: MODAL_VIEWS | undefined;
  data?: unknown;
  isOpen: boolean;
  headerProps?: Props;
}
type Action =
  | { type: "open"; view: MODAL_VIEWS; payload?: unknown; headerProps?: Props }
  | { type: "close" };

const initialState: State = {
  view: undefined,
  isOpen: false,
  data: null,
  headerProps: undefined,
};

function modalReducer(state: State, action: Action): State {
  switch (action.type) {
    case "open":
      return {
        ...state,
        view: action.view,
        data: action.payload,
        isOpen: true,
        headerProps: action.headerProps,
      };
    case "close":
      return {
        ...state,
        view: undefined,
        data: null,
        isOpen: false,
        headerProps: undefined,
      };
    default:
      throw new Error("Unknown Modal Action!");
  }
}

const ModalStateContext = React.createContext<State>(initialState);
ModalStateContext.displayName = "ModalStateContext";
const ModalActionContext = React.createContext<
  React.Dispatch<Action> | undefined
>(undefined);
ModalActionContext.displayName = "ModalActionContext";

export const ModalProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = React.useReducer(modalReducer, initialState);
  return (
    <ModalStateContext.Provider value={state}>
      <ModalActionContext.Provider value={dispatch}>
        {children}
      </ModalActionContext.Provider>
    </ModalStateContext.Provider>
  );
};

/* eslint-disable react-refresh/only-export-components */
export function useModalState() {
  const context = React.useContext(ModalStateContext);
  if (context === undefined) {
    throw new Error(`useModalState must be used within a ModalProvider`);
  }
  return context;
}

export function useModalAction() {
  const dispatch = React.useContext(ModalActionContext);

  if (dispatch === undefined) {
    throw new Error(`useModalAction must be used within a ModalProvider`);
  }

  return {
    openModal: (view: MODAL_VIEWS, payload?: unknown, headerProps?: Props) => {
      dispatch({ type: "open", view, payload, headerProps });
    },
    closeModal: () => {
      dispatch({ type: "close" });
    },
  };
}
